'use client'

// "VRS November deals" popup island: the modal plus the side tab it leaves
// behind when dismissed. Copy, image and the deal come from the sale-campaign
// doc (campaign.popup + limitedDeals); the state rules live in lib/dealPopup.js.
// This file is only the UI.
//
// Mounted from SiteChrome, so it runs on every page. The modal waits `delayMs`,
// and in opt-in consent regions it also waits for the cookie-consent choice, so
// it never stacks on top of the CMP modal. The tab needs no wait: it is small
// and only exists because the visitor already saw the modal.
//
// Client-only by design: nothing renders until mounted, so the time- and
// storage-dependent output never has to match the server HTML.

import React, { useEffect, useState } from 'react'
import SmartLink from '../SmartLink'
import DealCountdown from '../SaleCampaign/DealCountdown'
import DealPrice from '../SaleCampaign/DealPrice'
import { formatMoney } from '../sections/Sale/saleFormat'
import { pushDataLayerEvent } from '../../utils/dataLayer'
import { isOptInRequired } from '../../utils/consentMode'
import { getCookie } from 'cookies-next'
import { DEFAULT_REGION, REGION_COOKIE, isRegion, pathWithRegion } from '../../utils/regions'
import {
  STATE_NEW,
  STATE_TAB,
  STATE_OFF,
  getDealPopup,
  getPopupState,
  popupDelayMs,
  saveState,
  clearState,
} from '../../lib/dealPopup'
import styles from './styles.module.css'

const CONSENT_COOKIE_RE = /(?:^|;\s*)vrs_cc_cookie=/
const CONSENT_POLL_MS = 1000

const getStorage = () => {
  try {
    return window.localStorage
  } catch (_e) {
    return null
  }
}

const fill = (text, values) => String(text || '').replace(/\{(\w+)\}/g, (_m, k) => (values[k] != null ? values[k] : ''))

export default function DealPopup({ campaign = null, region = null, clockOffsetMs = 0 }) {
  const [open, setOpen] = useState(false)
  const [showTab, setShowTab] = useState(false)
  const [nowMs, setNowMs] = useState(null)
  const popup = campaign && campaign.popup
  const key = popup && popup.key
  const readNow = () => Date.now() + (Number(clockOffsetMs) || 0)

  // Prices need a store. Region pages pass it; content pages (/, /hardware,
  // /november-sales ...) have none in the URL, so resolve it like the region
  // switcher does: the region cookie, else geo-IP (/api/getRegion), else us.
  const [priceRegion, setPriceRegion] = useState(isRegion(region) ? region : null)
  useEffect(() => {
    if (isRegion(region)) {
      setPriceRegion(region)
      return undefined
    }
    const cookieRegion = getCookie(REGION_COOKIE)
    if (isRegion(cookieRegion)) {
      setPriceRegion(cookieRegion)
      return undefined
    }
    let cancelled = false
    fetch('/api/getRegion')
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!cancelled) setPriceRegion(body && isRegion(body.region) ? body.region : DEFAULT_REGION)
      })
      .catch(() => {
        if (!cancelled) setPriceRegion(DEFAULT_REGION)
      })
    return () => {
      cancelled = true
    }
  }, [region])

  useEffect(() => {
    if (!popup) return undefined
    // `?popup=show` forgets this browser's choice, so the modal can be previewed
    // again (QA, or sharing the campaign internally).
    if (new URLSearchParams(window.location.search).get('popup') === 'show') {
      clearState(getStorage(), popup)
    }
    setNowMs(readNow())
    const state = getPopupState({ campaign, nowMs: readNow(), region, pathname: window.location.pathname, storage: getStorage() })
    if (state === STATE_TAB) {
      setShowTab(true)
      return undefined
    }
    if (state !== STATE_NEW) return undefined

    let timer = null
    const consentPending = () => isOptInRequired() && !CONSENT_COOKIE_RE.test(document.cookie)
    const tryOpen = () => {
      if (consentPending()) {
        timer = setTimeout(tryOpen, CONSENT_POLL_MS)
        return
      }
      setOpen(true)
      pushDataLayerEvent('popup_view', { popup: key, trigger: 'auto' })
    }
    timer = setTimeout(tryOpen, popupDelayMs(popup))
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, region, clockOffsetMs])

  // The countdown ticks only while the modal is open.
  useEffect(() => {
    if (!open) return undefined
    const id = setInterval(() => setNowMs(readNow()), 1000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clockOffsetMs])

  // Any way of closing the modal without clicking through leaves the side tab.
  const dismiss = (reason) => {
    saveState(getStorage(), popup, STATE_TAB)
    setOpen(false)
    setShowTab(true)
    pushDataLayerEvent('popup_dismiss', { popup: key, reason })
  }

  const reopen = () => {
    setShowTab(false)
    setNowMs(readNow())
    setOpen(true)
    pushDataLayerEvent('popup_view', { popup: key, trigger: 'tab' })
  }

  const closeTab = () => {
    saveState(getStorage(), popup, STATE_OFF)
    setShowTab(false)
    pushDataLayerEvent('popup_dismiss', { popup: key, reason: 'tab_close' })
  }

  // Clicked through to the deal: no modal and no tab on this browser again.
  const goToDeal = (from) => {
    saveState(getStorage(), popup, STATE_OFF)
    setOpen(false)
    setShowTab(false)
    pushDataLayerEvent('popup_click', { popup: key, from })
  }

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss('escape')
    }
    document.addEventListener('keydown', onKey)
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (nowMs === null || (!open && !showTab)) return null
  const view = getDealPopup(campaign, nowMs, priceRegion)
  if (!view) return null

  const { live, deals, fromPrice, targetMs } = view
  const values = { price: formatMoney(fromPrice) || '' }
  const href = region ? pathWithRegion(popup.productPath, region) : popup.productPath

  if (showTab) {
    return (
      <div className={styles.tab}>
        <button
          type="button"
          className={styles.tabOpen}
          onClick={reopen}
          aria-label={`${popup.tabLabel}: ${fill(fromPrice ? popup.tabCta : popup.tabCtaNoPrice, values)}`}
        >
          {/* Square split down the middle: the wordmark on paper, the pedals photo. */}
          <span className={styles.tabWord} aria-hidden="true">
            <span className={styles.tabBrand}>{popup.brand}</span>
            <span className={styles.tabMonth}>{popup.month}</span>
            <span className={styles.tabTag}>{popup.tag}</span>
          </span>
          <span className={styles.tabMedia} aria-hidden="true">
            {/* eslint-disable-next-line @next/next/no-img-element, local-rules/prefer-img-component -- decorative campaign thumb in a fixed overlay */}
            <img src={popup.image} alt="" width={68} height={136} decoding="async" />
          </span>
        </button>
        <button type="button" className={styles.tabClose} onClick={closeTab} aria-label="Hide">
          ×
        </button>
      </div>
    )
  }

  return (
    <div className={styles.backdrop} onClick={() => dismiss('backdrop')} role="presentation">
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vrs-popup-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className={styles.close} onClick={() => dismiss('close')} aria-label="Close">
          ×
        </button>

        <div className={styles.media} aria-hidden="true">
          {/* Absolutely positioned, object-fit: cover inside a fixed modal: it
              cannot shift layout. Static campaign still, no descriptor. */}
          {/* eslint-disable-next-line @next/next/no-img-element, local-rules/prefer-img-component -- decorative campaign still in a fixed overlay */}
          <img src={popup.image} alt="" width={760} height={802} decoding="async" />
          <span className={styles.mediaPill}>{live ? popup.eyebrowLive : popup.eyebrowTeaser}</span>
        </div>

        <div className={styles.panel}>
          <div className={styles.wordmark}>
            <span className={styles.brand}>{popup.brand}</span>
            <span className={styles.month}>{popup.month}</span>
            <span className={styles.tagWord}>{popup.tag}</span>
          </div>

          <p className={styles.eyebrow}>{fill(live ? popup.subLive : popup.subTeaser, values)}</p>
          <h2 id="vrs-popup-title" className={styles.title}>{popup.title}</h2>

          {/* Headline deal first (bigger), then the others: name, deal price,
              regular price struck through and the % off. */}
          <ul className={styles.deals}>
            {deals.map((d, i) => (
              <li key={d.itemType} className={i === 0 ? styles.dealMain : styles.deal}>
                <span className={styles.dealName}>{d.title}</span>
                <DealPrice price={d.price} regular={d.regular} size="sm" theme="light" className={styles.dealPriceBlock}/>
              </li>
            ))}
          </ul>

          <p className={styles.lead}>{fill(popup.lead, values)}</p>

          <DealCountdown
            className={styles.countdown}
            label={live ? popup.countdownLive : popup.countdownTeaser}
            targetMs={targetMs}
            nowMs={nowMs}
          />

          {Array.isArray(popup.bullets) && popup.bullets.length > 0 && (
            <ul className={styles.bullets}>
              {popup.bullets.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}

          <SmartLink href={href} className={styles.submit} onClick={() => goToDeal('modal')}>
            {live ? popup.cta : popup.ctaTeaser}
          </SmartLink>
          <button type="button" className={styles.decline} onClick={() => dismiss('decline')}>
            {popup.decline}
          </button>
        </div>
      </div>
    </div>
  )
}
